import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Article (Analytics, mapped to the "Technical" goal token).
 * Statistical process control + correlation on existing sensor/heat data: an
 * SPC-style line chart with control limits, and how drift precedes defects.
 * Honest, vendor-neutral body; product only appears in the closing block.
 * All numbers are illustrative.
 */
export const predictingDefectsSpcAndCorrelations: Article = {
  slug: "predicting-defects-spc-and-correlations",
  title: "Predicting defects: SPC, correlations, and the data you already have",
  deck:
    "You do not need a machine-learning team or a new sensor budget to start predicting defects. Two well-worn statistical tools — control charts and correlation — applied to the data already coming off your line will get you surprisingly far.",
  goal: "Technical",
  readingMins: 9,
  heroStat: { value: "0", label: "new sensors required to start finding the signal" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "There is a tempting story that predicting defects requires artificial intelligence, a data-science hire, and a fresh layer of instrumentation. For most foundries that story is both expensive and wrong. The uncomfortable truth is that the data needed to anticipate a large share of your rejects is almost certainly already being collected — pour temperatures, sand moisture, charge weights, cycle times — and the tools needed to read it are older than the people reading them. Statistical process control and correlation analysis are not glamorous, but applied honestly to the data you already have, they turn defects from surprises into things you saw coming.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Tool one",
      heading: "Statistical process control: a chart that knows what 'normal' is",
      sub: "Control limits are the voice of the process, not a target someone picked.",
    },
    {
      kind: "paragraph",
      text:
        "Statistical process control, or SPC, rests on a single powerful idea: a stable process varies within predictable bounds, and those bounds can be calculated from the process's own history rather than guessed. Plot a parameter over time, draw a centre line at its mean and control limits a few standard deviations above and below, and you have a chart that distinguishes ordinary noise from a genuine signal. The limits are not a quality target — they are a statement of what the process actually does when nothing is wrong. The moment a point steps outside them, or a run of points drifts steadily toward one, the process is telling you it has changed.",
    },
    {
      kind: "callout",
      tone: "info",
      title: "Control limits ≠ specification limits",
      text:
        "Specification limits come from the customer's drawing — what the part must be. Control limits come from the process's own behaviour — what it normally does. A process can be drifting dangerously while every part is still in spec. SPC catches that drift before the spec is breached.",
    },
    {
      kind: "paragraph",
      text:
        "The reason SPC predicts defects is that processes rarely fail instantly. A furnace lining wears, a sand system slowly loses moisture, a die warms across a shift — and the relevant parameter drifts before any casting actually goes bad. On an SPC chart that drift is visible as a trend creeping toward a limit, often hours before the first reject appears at inspection. That window between 'the chart looks wrong' and 'the parts are wrong' is exactly where prediction lives. You are not forecasting the future from nothing; you are reading an early warning the process is already broadcasting.",
    },
    {
      kind: "chart",
      chart: "line",
      title: "Illustrative: pour temperature drifting toward a control limit before rejects appear",
      xKey: "heat",
      unit: "°C",
      series: [
        { dataKey: "value", label: "Pour temperature", tone: "brand" },
        { dataKey: "ucl", label: "Upper control limit", tone: "danger" },
        { dataKey: "center", label: "Centre line", tone: "info" },
        { dataKey: "lcl", label: "Lower control limit", tone: "danger" },
      ],
      data: [
        { heat: "H1", value: 1418, ucl: 1445, center: 1415, lcl: 1385 },
        { heat: "H2", value: 1412, ucl: 1445, center: 1415, lcl: 1385 },
        { heat: "H3", value: 1416, ucl: 1445, center: 1415, lcl: 1385 },
        { heat: "H4", value: 1421, ucl: 1445, center: 1415, lcl: 1385 },
        { heat: "H5", value: 1428, ucl: 1445, center: 1415, lcl: 1385 },
        { heat: "H6", value: 1435, ucl: 1445, center: 1415, lcl: 1385 },
        { heat: "H7", value: 1442, ucl: 1445, center: 1415, lcl: 1385 },
        { heat: "H8", value: 1449, ucl: 1445, center: 1415, lcl: 1385 },
      ],
      caption:
        "Generic, illustrative data. The reading climbs through the centre line and crosses the upper limit at H8 — the trend was visible several heats before the breach, which is where the warning lives.",
    },
    {
      kind: "pullQuote",
      quote:
        "A defect is usually the last event in a drift, not the first sign of trouble. SPC lets you act on the drift instead of waiting for the defect.",
      attribution: "The logic of early warning",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Tool two",
      heading: "Correlation: which knob actually moves the defect",
      sub: "SPC tells you something changed. Correlation tells you what to change back.",
    },
    {
      kind: "paragraph",
      text:
        "SPC flags that a parameter has drifted, but it does not tell you which parameter is responsible for which defect. That is the job of correlation analysis: lining up your process variables against your defect outcomes across many heats and asking, statistically, which ones move together. When sand moisture and gas porosity rise and fall in step across hundreds of heats, you have a lead. When pour temperature tracks misruns, you have another. Correlation does not prove cause on its own — but it points the metallurgist's attention at the two or three variables worth investigating, instead of the fifty that are merely being logged.",
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Correlation is a lead, not a verdict",
      text:
        "Two variables moving together can share a hidden third cause, or line up by chance. Treat a strong correlation as a hypothesis to test with process knowledge and a controlled change — not as proof. Used that way, it is one of the most efficient ways to narrow a search.",
    },
    {
      kind: "paragraph",
      text:
        "The two tools are far stronger together than apart. Correlation, run over your history, tells you which handful of parameters actually drive your worst defects — that is where to point your attention. SPC then watches exactly those parameters in real time and raises a hand the moment one of them drifts. You end up monitoring the few variables that matter, with limits derived from your own line, catching the drift before it becomes scrap. No new sensors, no model that needs a PhD to maintain — just disciplined statistics on data you are already paying to collect.",
    },
    {
      kind: "metricRow",
      metrics: [
        {
          value: "3σ",
          label: "Typical control band",
          caption: "Limits set three standard deviations either side of the mean capture ~99.7% of normal variation.",
          tone: "info",
        },
        {
          value: "7-in-a-row",
          label: "Classic drift rule",
          caption: "Seven consecutive points trending one way flags a shift long before any limit is crossed.",
          tone: "warning",
        },
        {
          value: "2–3",
          label: "Variables that dominate",
          caption: "A short list of correlated parameters usually accounts for most of a given defect.",
          tone: "brand",
        },
      ],
    },
    {
      kind: "sectionHeading",
      eyebrow: "Getting started",
      heading: "The hard part is wiring, not maths",
    },
    {
      kind: "paragraph",
      text:
        "If the statistics are this approachable, why doesn't every plant already do this? The honest answer is that the maths was never the obstacle — the plumbing was. Computing a control chart is trivial; getting clean, time-aligned readings for each parameter, tied to the heat and the defect outcome, is the work. When your data lives in end-of-shift spreadsheets with no link between the pour temperature and the casting it produced, correlation is impossible and SPC is a manual chore nobody keeps up. Solve the data-plumbing problem and both tools fall out almost for free. That is the order of operations: get the data attached and aligned first, and the analytics become the easy part.",
    },
    {
      kind: "chart",
      chart: "bar",
      title: "Illustrative: correlation strength of process variables against gas porosity",
      xKey: "variable",
      series: [{ dataKey: "strength", label: "Correlation strength (0–1)", tone: "accent" }],
      data: [
        { variable: "Sand moisture", strength: 0.71 },
        { variable: "Pour temp", strength: 0.44 },
        { variable: "Hold time", strength: 0.33 },
        { variable: "Charge ratio", strength: 0.19 },
        { variable: "Ambient humidity", strength: 0.12 },
      ],
      caption:
        "Generic, illustrative coefficients. One or two variables standing well above the rest is the typical pattern — and exactly the shortlist worth watching with SPC.",
    },
    {
      kind: "paragraph",
      text:
        "Predicting defects, then, is less exotic than the marketing suggests. Find the few variables that correlate with your worst rejects, put control limits around them derived from your own process, and watch for the drift that precedes the defect. The tools are decades old and well understood; the only modern requirement is data that is clean, aligned and tied to outcomes. Get that, and you move from explaining yesterday's scrap to heading off tomorrow's.",
    },
    {
      kind: "sahasEnables",
      intro:
        "SPC and correlation are open, vendor-neutral techniques — anyone can run them. What the platform removes is the plumbing that usually stops them: getting clean, aligned, outcome-linked data into one place so the statistics can actually run.",
      capabilities: [
        {
          title: "Aligned, outcome-linked data",
          detail:
            "Readings stream onto the model and stay tied to the heat, mould and defect outcome — the alignment SPC and correlation both depend on, built in.",
          icon: "database",
        },
        {
          title: "Correlation engine",
          detail:
            "A commonality and correlation view surfaces which process variables actually move each defect, so the metallurgist's shortlist is generated, not guessed.",
          icon: "lineChart",
        },
        {
          title: "Live SPC on the variables that matter",
          detail:
            "Control charts run continuously on the parameters that correlate with your defects, raising a flag on drift before it reaches the scrap bin.",
          icon: "gauge",
        },
      ],
    },
  ],
};
